import React, { useEffect, useRef } from "react";
import { Box, Button, Card, Divider, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { QrCodeScanner } from "@mui/icons-material";
import { useFormikContext } from "formik";
import { getAccessToken, useAuth } from "@/context/auth";
import { GetVehicle } from "@/services/api/vehicle-service";
import { asImageDataUrl } from "../../helpers/genericForm.helper";

type Props = {
  label: string;
  required?: boolean;
  licenseDiskDoc?: string;
  onScan: () => void;
  isComplete: () => boolean;
  children?: React.ReactNode; // VehicleFields
};

export default function VehicleScanCard({
  label,
  required,
  licenseDiskDoc,
  onScan,
  isComplete,
  children,
}: Props) {
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const { setFieldValue } = useFormikContext<Record<string, unknown>>();
  const lastHydratedVehicleIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const readVehicleId = () => {
      const raw = window.localStorage.getItem("dynamicViewVehicleId");
      if (!raw) return "";
      try {
        const j = JSON.parse(raw);
        return (typeof j === "string" ? j : String(j ?? "")).trim();
      } catch {
        return raw.trim();
      }
    };

    const hydrateVehicle = async () => {
      if (!token) return;

      const vehicleId = readVehicleId();
      if (
        !vehicleId ||
        vehicleId === "00000000-0000-0000-0000-000000000000" ||
        lastHydratedVehicleIdRef.current === vehicleId
      ) {
        return;
      }

      const resp = await GetVehicle(token, vehicleId);
      const d = (((resp as { data?: any })?.data?.data ??
        (resp as { data?: any })?.data ??
        {}) as Record<string, unknown>);
      if (cancelled) return;

      lastHydratedVehicleIdRef.current = vehicleId;

      setFieldValue("vehicleId", vehicleId, false);
      setFieldValue("licensePlateNumber", String(d.licensePlateNumber ?? ""), false);
      setFieldValue("make", String(d.make ?? ""), false);
      setFieldValue("model", String(d.model ?? ""), false);
      setFieldValue("color", String(d.color ?? ""), false);
      setFieldValue("vin", String(d.vin ?? ""), false);
      setFieldValue("engineNumber", String(d.engineNumber ?? ""), false);
      setFieldValue("licenseDiskDoc", asImageDataUrl(String(d.licenseDiskDoc ?? "")), false);
    };

    void hydrateVehicle().catch((err: unknown) => {
      if (err instanceof Error) console.error(err.message);
    });

    return () => {
      cancelled = true;
    };
  }, [token, setFieldValue]);

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Grid container justifyContent="center" alignItems="center" direction="column">
        <Grid>
          <Typography variant="body1">{label}</Typography>
        </Grid>
        <Divider sx={{ pb: 3, width: "100%" }} />
      </Grid>

      <Button
        style={{ borderRadius: 3 }}
        startIcon={<QrCodeScanner />}
        onClick={onScan}
        variant="contained"
        color="secondary"
        fullWidth
        size="large"
      >
        <strong>Scan License Disc</strong>
      </Button>

      {required && !isComplete() && (
        <Typography sx={{ mt: 1 }} color="error" variant="body2">
          This step is required. Please scan the license disc to continue.
        </Typography>
      )}

      {licenseDiskDoc && (
        <>
          <br />
          <Divider sx={{ pb: 3 }} />
          <Box sx={{ width: "100%", maxHeight: 420, aspectRatio: "4 / 3" }}>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                bgcolor: "grey.200",
                borderRadius: 2,
                overflow: "hidden",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                alt="license-disk"
                src={licenseDiskDoc}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          </Box>
        </>
      )}

      {children ? (
        <>
          <br />
          <br />
          {children}
        </>
      ) : null}
    </Card>
  );
}
